import React, { useState } from 'react';
import { Pencil, Plus, X } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { InputNode, NodeLayoutStyle } from '../../types';
import { DocumentInputRenderer } from '../../renderer/DocumentInputRenderer';
import { DocumentRendererContext } from '../../renderer/types';
import { Checkbox } from '../../../ui/checkbox';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { cn } from '../../../../lib/utils';

const REPEATER_CARD_CLASS = '';
const REPEATER_ITEM_CLASS =
    'flex items-start gap-3 rounded-lg border border-border/50 bg-background/35 p-3';
const REPEATER_ITEM_ACCENT_CLASS =
    'min-h-10 w-1 self-stretch rounded-full bg-[#6AA2FF]';
const REPEATER_CHECKLIST_CONTAINER_CLASS = 'space-y-3';
const REPEATER_CHECKLIST_ITEM_CLASS = 'flex items-start gap-2';
const REPEATER_MANUAL_FIELDS_CLASS = 'flex-1 min-w-0';
const REPEATER_SECONDARY_TEXT_CLASS = 'text-[#A3A3A3]';
const REPEATER_EMPHASIS_TEXT_CLASS = 'font-semibold text-[#86E8A8]';
const REPEATER_INPUT_CLASS =
    'border-border/50 bg-background/55 dark:border-white/10 dark:bg-[var(--main-grey)]';
const REPEATER_PRIMARY_ACTION_CLASS =
    'mb-[2px] border border-[#6AA2FF] bg-[#6AA2FF] text-white shadow-none hover:border-[#5D93F2] hover:bg-[#5D93F2] hover:text-white';
const DELETE_BUTTON_CLASS =
    'rounded-lg bg-transparent text-[#8F8EA2] hover:bg-destructive/10 hover:text-destructive';
const DELETE_ICON_CLASS = 'h-4 w-4';

interface DocumentRepeaterProps {
    field: InputNode;
    value: any[];
    onChange: (value: any[]) => void;
    disabled?: boolean;
    checkHidden?: (expr?: string, data?: any) => boolean;
    context: DocumentRendererContext;
}

export function DocumentRepeater({
    field,
    value = [],
    onChange,
    disabled,
    checkHidden = () => false,
    context,
}: DocumentRepeaterProps) {
    const [newItem, setNewItem] = useState<Record<string, any>>({});
    const [customValue, setCustomValue] = useState('');
    const [isLoadingConditionals, setIsLoadingConditionals] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);

    const schemaFields = field.fields || [];
    const mode = field.repeater?.mode || 'manual';
    const optionFieldId = field.repeater?.optionFieldId || schemaFields[0]?.id;
    const customFieldId = field.repeater?.customValueFieldId || optionFieldId;
    const detailFieldId = field.repeater?.detailFieldId;
    const allowCustomValues = !!field.repeater?.allowCustomValues;
    const dedupeByFieldId = field.repeater?.dedupeByFieldId;
    const customPlaceholder = 'Add custom...';

    const optionField = schemaFields.find((f) => f.id === optionFieldId) as
        | InputNode
        | undefined;
    const optionValues = optionField?.data?.options?.fixed || [];
    const detailField = schemaFields.find((f) => f.id === detailFieldId) as
        | InputNode
        | undefined;

    const getColSpanClass = (layout?: NodeLayoutStyle) => {
        if (!layout || layout.width === '100%') return 'sm:col-span-2';
        return '';
    };

    const isRequired = (inputField: InputNode) => {
        if (!inputField.validation) return false;
        return inputField.validation.some((rule) => rule.type === 'required');
    };

    const isEmphasisField = (inputField: InputNode) =>
        inputField.inputType === 'number' || inputField.id === 'minutes';

    const resolveItemId = (item: Record<string, any>, index: number) =>
        item?.id || `repeater-${index}`;

    const createItemId = () =>
        `${Date.now().toString()}-${Math.random().toString(36).slice(2, 8)}`;

    const getAddHandlerKey = () =>
        field.functionKey ||
        (field.id === 'implants' ? 'appendImplantConditionBlock' : undefined);

    const getRemoveHandlerKey = () =>
        field.functionKey ||
        (field.id === 'implants' ? 'removeImplantConditionBlock' : undefined);

    const runAddHandler = (itemToAdd: Record<string, any>, itemId: string) => {
        const addHandlerKey = getAddHandlerKey();
        if (
            addHandlerKey &&
            context.functions.repeaterHandlers[addHandlerKey]
        ) {
            context.functions.repeaterHandlers[addHandlerKey]({
                field,
                item: itemToAdd,
                itemId,
                data: context.contextData,
                watch: context.watch,
                updateData: context.updateData,
                setValue: context.setValue,
            });
        }
    };

    const runRemoveHandler = (
        itemToRemove: Record<string, any>,
        itemId: string
    ) => {
        const removeHandlerKey = getRemoveHandlerKey();
        if (
            removeHandlerKey &&
            context.functions.repeaterHandlers[removeHandlerKey]
        ) {
            context.functions.repeaterHandlers[removeHandlerKey]({
                field,
                item: itemToRemove,
                itemId,
                data: context.contextData,
                watch: context.watch,
                updateData: context.updateData,
                setValue: context.setValue,
            });
        }
    };

    const removeItemsByPredicate = (
        predicate: (item: Record<string, any>) => boolean
    ) => {
        const itemsToRemove = value.filter(predicate);
        if (itemsToRemove.length === 0) return;

        itemsToRemove.forEach((item, index) =>
            runRemoveHandler(item, item.id || `remove-${index}`)
        );
        onChange(value.filter((item) => !predicate(item)));
    };

    const addItem = (
        nextItem: Record<string, any>,
        options?: { toggleOnDuplicate?: boolean }
    ) => {
        if (!nextItem || Object.keys(nextItem).length === 0) return;

        const dedupeField = dedupeByFieldId;
        if (dedupeField) {
            const duplicate = value.find(
                (item) => item?.[dedupeField] === nextItem?.[dedupeField]
            );
            if (duplicate) {
                if (options?.toggleOnDuplicate) {
                    removeItemsByPredicate(
                        (item) =>
                            item?.[dedupeField] === nextItem?.[dedupeField]
                    );
                }
                return;
            }
        }

        const itemId = nextItem.id || createItemId();
        const itemToAdd = { id: itemId, ...nextItem };
        runAddHandler(itemToAdd, itemId);
        onChange([...value, itemToAdd]);
    };

    const handleRemoveItem = (itemId: string) => {
        const entryIndex = value.findIndex(
            (item, index) => resolveItemId(item, index) === itemId
        );
        if (entryIndex === -1) return;

        const entry = value[entryIndex];
        runRemoveHandler(entry, entry.id || itemId);
        onChange(value.filter((_, index) => index !== entryIndex));
    };

    const handleFieldChange = (fieldId: string, val: any) => {
        setNewItem((prev) => ({ ...prev, [fieldId]: val }));
    };

    const handleItemFieldChange = (
        itemId: string,
        fieldId: string,
        val: any
    ) => {
        onChange(
            value.map((item, index) =>
                resolveItemId(item, index) === itemId
                    ? { ...item, id: item.id || itemId, [fieldId]: val }
                    : item
            )
        );
    };

    const handleContextUpdate = (updates: Record<string, any>) => {
        setNewItem((prev) => ({ ...prev, ...updates }));
    };

    const handleItemContextUpdate = (
        itemId: string,
        updates: Record<string, any>
    ) => {
        onChange(
            value.map((item, index) =>
                resolveItemId(item, index) === itemId
                    ? { ...item, id: item.id || itemId, ...updates }
                    : item
            )
        );
    };

    const handleAddManualItem = () => {
        if (isLoadingConditionals) return;

        const isValid = schemaFields.every((schemaField) => {
            if (
                schemaField.hidden &&
                checkHidden(schemaField.hidden, newItem)
            ) {
                return true;
            }
            const itemValue = newItem[schemaField.id];
            if (isRequired(schemaField as InputNode)) {
                return itemValue !== undefined && itemValue !== '';
            }
            return true;
        });

        if (!isValid) return;
        addItem(newItem);
        setNewItem({});
    };

    const handleChecklistToggle = (checked: boolean, optionValue: string) => {
        if (!optionFieldId) return;

        if (checked) {
            const alreadySelected = value.some(
                (item) => item?.[optionFieldId] === optionValue
            );
            if (alreadySelected) return;
            addItem({ [optionFieldId]: optionValue });
            return;
        }

        removeItemsByPredicate((item) => item?.[optionFieldId] === optionValue);
    };

    const handleChipOptionClick = (optionValue: string) => {
        if (!optionFieldId) return;
        addItem(
            { [optionFieldId]: optionValue },
            { toggleOnDuplicate: !!dedupeByFieldId }
        );
    };

    const handleAddCustomItem = () => {
        const trimmedValue = customValue.trim();
        if (!trimmedValue || !customFieldId) return;

        const itemToAdd: Record<string, any> = {
            [customFieldId]: trimmedValue,
        };

        if (optionFieldId && optionFieldId !== customFieldId) {
            itemToAdd[optionFieldId] = trimmedValue;
        }

        addItem(itemToAdd, { toggleOnDuplicate: !!dedupeByFieldId });
        setCustomValue('');
    };

    const handleStartAdd = () => {
        setNewItem({});
        setEditingItemId(null);
        setIsFormOpen(true);
    };

    const handleStartEdit = (itemId: string) => {
        const item = value.find(
            (v, index) => resolveItemId(v, index) === itemId
        );
        if (!item) return;
        setNewItem({ ...item });
        setEditingItemId(itemId);
        setIsFormOpen(true);
    };

    const handleCancelForm = () => {
        setNewItem({});
        setEditingItemId(null);
        setIsFormOpen(false);
    };

    const handleSaveOrUpdate = () => {
        if (isLoadingConditionals) return;

        const isValid = schemaFields.every((schemaField) => {
            if (
                schemaField.hidden &&
                checkHidden(schemaField.hidden, newItem)
            ) {
                return true;
            }
            const itemValue = newItem[schemaField.id];
            if (isRequired(schemaField as InputNode)) {
                return itemValue !== undefined && itemValue !== '';
            }
            return true;
        });

        if (!isValid) return;

        if (editingItemId) {
            onChange(
                value.map((item, index) =>
                    resolveItemId(item, index) === editingItemId
                        ? { ...newItem, id: item.id || editingItemId }
                        : item
                )
            );
        } else {
            addItem(newItem);
        }

        setNewItem({});
        setEditingItemId(null);
        setIsFormOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter') return;

        e.preventDefault();
        if (mode === 'manual') {
            handleSaveOrUpdate();
            return;
        }

        if (mode === 'chip-add' && allowCustomValues) {
            handleAddCustomItem();
        }
    };

    const renderManualItems = () => {
        if (value.length === 0) return null;

        return (
            <div className="space-y-2">
                {value.map((item, index) => {
                    const itemId = resolveItemId(item, index);
                    const isEditing = editingItemId === itemId && isFormOpen;
                    return (
                        <div
                            key={itemId}
                            className={cn(
                                REPEATER_ITEM_CLASS,
                                isEditing &&
                                    'border-[#6AA2FF]/50 bg-[#6AA2FF]/[0.06]'
                            )}
                        >
                            <div
                                className={cn(
                                    REPEATER_ITEM_ACCENT_CLASS,
                                    isEditing && 'bg-[#6AA2FF]'
                                )}
                            />
                            <div className={REPEATER_MANUAL_FIELDS_CLASS}>
                                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                                    {schemaFields.reduce<React.ReactNode[]>(
                                        (nodes, schemaField) => {
                                            if (
                                                schemaField.hidden &&
                                                checkHidden(
                                                    schemaField.hidden,
                                                    item
                                                )
                                            )
                                                return nodes;

                                            const currentValue =
                                                item[schemaField.id];
                                            if (
                                                currentValue === undefined ||
                                                currentValue === null ||
                                                currentValue === ''
                                            )
                                                return nodes;

                                            const inputField =
                                                schemaField as InputNode;
                                            const isTextarea =
                                                inputField.inputType ===
                                                'textarea';
                                            const isBadge =
                                                inputField.inputType ===
                                                    'radio' ||
                                                inputField.inputType ===
                                                    'select';
                                            const needsDivider =
                                                nodes.length > 0;

                                            if (needsDivider) {
                                                nodes.push(
                                                    <div
                                                        key={`div-${schemaField.id}`}
                                                        className="sm:col-span-2 border-t border-border/30"
                                                    />
                                                );
                                            }

                                            if (isTextarea) {
                                                nodes.push(
                                                    <div
                                                        key={schemaField.id}
                                                        className="sm:col-span-2 pt-1"
                                                    >
                                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-1">
                                                            {schemaField.title}
                                                        </div>
                                                        <div className="text-sm font-medium leading-relaxed whitespace-pre-wrap">
                                                            {currentValue}
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                nodes.push(
                                                    <span
                                                        key={`label-${schemaField.id}`}
                                                        className={cn(
                                                            'text-sm py-1.5',
                                                            REPEATER_SECONDARY_TEXT_CLASS
                                                        )}
                                                    >
                                                        {schemaField.title}
                                                    </span>,
                                                    <span
                                                        key={`value-${schemaField.id}`}
                                                        className="text-sm py-1.5"
                                                    >
                                                        {isBadge ? (
                                                            <span className="inline-flex rounded-full border border-border/50 px-2.5 py-0.5 text-xs font-medium">
                                                                {currentValue}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className={cn(
                                                                    'font-medium',
                                                                    isEmphasisField(
                                                                        inputField
                                                                    ) &&
                                                                        REPEATER_EMPHASIS_TEXT_CLASS
                                                                )}
                                                            >
                                                                {currentValue}
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            }

                                            return nodes;
                                        },
                                        []
                                    )}
                                </div>
                            </div>

                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartEdit(itemId)}
                                disabled={disabled}
                                type="button"
                                className={DELETE_BUTTON_CLASS}
                            >
                                <Pencil className={DELETE_ICON_CLASS} />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(itemId)}
                                disabled={disabled}
                                type="button"
                                className={DELETE_BUTTON_CLASS}
                            >
                                <X className={DELETE_ICON_CLASS} />
                            </Button>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderChecklistMode = () => {
        if (!optionFieldId || optionValues.length === 0) return null;

        return (
            <div className={REPEATER_CHECKLIST_CONTAINER_CLASS}>
                {optionValues.map((option) => {
                    const selectedItem = value.find(
                        (item) => item?.[optionFieldId] === option.value
                    );
                    const isChecked = !!selectedItem;
                    const optionId = `${field.id}-${option.value}`;

                    return (
                        <div key={option.value} className="space-y-2">
                            <div className={REPEATER_CHECKLIST_ITEM_CLASS}>
                                <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id={optionId}
                                            checked={isChecked}
                                            disabled={disabled}
                                            onCheckedChange={(checked) =>
                                                handleChecklistToggle(
                                                    Boolean(checked),
                                                    option.value
                                                )
                                            }
                                        />
                                        <Label
                                            htmlFor={optionId}
                                            className="cursor-pointer font-normal text-foreground/90"
                                        >
                                            {option.label}
                                        </Label>
                                    </div>

                                    {isChecked &&
                                        detailField &&
                                        !(
                                            detailField.hidden &&
                                            checkHidden(
                                                detailField.hidden,
                                                selectedItem
                                            )
                                        ) && (
                                            <div className="pl-6">
                                                <DocumentInputRenderer
                                                    field={detailField}
                                                    value={
                                                        selectedItem?.[
                                                            detailField.id
                                                        ] || ''
                                                    }
                                                    onChange={(val) =>
                                                        handleItemFieldChange(
                                                            resolveItemId(
                                                                selectedItem,
                                                                value.findIndex(
                                                                    (item) =>
                                                                        item?.[
                                                                            optionFieldId
                                                                        ] ===
                                                                        option.value
                                                                )
                                                            ),
                                                            detailField.id,
                                                            val
                                                        )
                                                    }
                                                    context={context}
                                                    onContextUpdate={(
                                                        updates
                                                    ) =>
                                                        handleItemContextUpdate(
                                                            resolveItemId(
                                                                selectedItem,
                                                                value.findIndex(
                                                                    (item) =>
                                                                        item?.[
                                                                            optionFieldId
                                                                        ] ===
                                                                        option.value
                                                                )
                                                            ),
                                                            updates
                                                        )
                                                    }
                                                    onLoadingChange={
                                                        setIsLoadingConditionals
                                                    }
                                                />
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderChipDetailsList = () => {
        if (value.length === 0) return null;

        return (
            <div className="space-y-3">
                {value.map((item, index) => {
                    const itemId = resolveItemId(item, index);
                    const optionLabel =
                        optionFieldId && item?.[optionFieldId]
                            ? item[optionFieldId]
                            : `Item ${index + 1}`;

                    const fieldsToRender = schemaFields.filter(
                        (schemaField) => {
                            if (schemaField.id === optionFieldId) return false;
                            if (
                                schemaField.hidden &&
                                checkHidden(schemaField.hidden, item)
                            ) {
                                return false;
                            }
                            return true;
                        }
                    );

                    return (
                        <div key={itemId} className={REPEATER_ITEM_CLASS}>
                            <div className={REPEATER_ITEM_ACCENT_CLASS} />
                            <div className="flex-1 space-y-3">
                                <div className="text-sm font-medium">
                                    {optionLabel}
                                </div>

                                {fieldsToRender.length > 0 && (
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        {fieldsToRender.map((schemaField) => (
                                            <div
                                                key={schemaField.id}
                                                className={`min-w-0 ${getColSpanClass(
                                                    schemaField.style?.layout
                                                )}`}
                                            >
                                                <label className="text-xs font-medium mb-1.5 block">
                                                    {schemaField.title}
                                                    {isRequired(
                                                        schemaField as InputNode
                                                    ) && (
                                                        <span className="text-destructive ml-1">
                                                            *
                                                        </span>
                                                    )}
                                                </label>

                                                <DocumentInputRenderer
                                                    field={
                                                        schemaField as InputNode
                                                    }
                                                    value={
                                                        item[schemaField.id] ||
                                                        ''
                                                    }
                                                    onChange={(val) =>
                                                        handleItemFieldChange(
                                                            itemId,
                                                            schemaField.id,
                                                            val
                                                        )
                                                    }
                                                    context={context}
                                                    onContextUpdate={(
                                                        updates
                                                    ) =>
                                                        handleItemContextUpdate(
                                                            itemId,
                                                            updates
                                                        )
                                                    }
                                                    onLoadingChange={
                                                        setIsLoadingConditionals
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(itemId)}
                                disabled={disabled}
                                type="button"
                                className={DELETE_BUTTON_CLASS}
                            >
                                <X className={DELETE_ICON_CLASS} />
                            </Button>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderChipMode = () => {
        if (!optionFieldId || optionValues.length === 0) return null;

        return (
            <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                    {optionValues.map((option) => {
                        const isSelected =
                            !!dedupeByFieldId &&
                            value.some(
                                (item) =>
                                    item?.[dedupeByFieldId] === option.value
                            );

                        return (
                            <Button
                                key={option.value}
                                type="button"
                                size="sm"
                                variant={isSelected ? 'secondary' : 'outline'}
                                onClick={() =>
                                    handleChipOptionClick(option.value)
                                }
                                disabled={disabled}
                                className={cn(
                                    'border-border/50 bg-background/45 hover:bg-muted/55 dark:border-white/10 dark:bg-[var(--main-grey)] dark:hover:bg-[var(--bright-grey)]',
                                    isSelected &&
                                        'border-white/12 bg-[var(--bright-grey)] text-foreground hover:bg-[#2B2B2B]'
                                )}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                {option.label}
                            </Button>
                        );
                    })}
                </div>

                {allowCustomValues && customFieldId && (
                    <div className="flex items-center gap-2">
                        <Input
                            value={customValue}
                            placeholder={customPlaceholder}
                            onChange={(e) => setCustomValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={disabled}
                            className={cn('flex-1', REPEATER_INPUT_CLASS)}
                        />
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className={REPEATER_PRIMARY_ACTION_CLASS}
                            onClick={handleAddCustomItem}
                            disabled={disabled || !customValue.trim()}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Save
                        </Button>
                    </div>
                )}

                {renderChipDetailsList()}
            </div>
        );
    };

    const renderManualForm = () => {
        const formTitle = editingItemId
            ? `Edit ${field.title || 'item'}`
            : `New ${field.title || 'item'}`;
        const submitLabel = editingItemId
            ? 'Update'
            : `Add ${field.title || 'item'}`;

        return (
            <div className="rounded-lg border border-border/50 bg-background/35">
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                    <span className="text-sm font-medium">{formTitle}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelForm}
                        type="button"
                        className={DELETE_BUTTON_CLASS}
                    >
                        <X className={DELETE_ICON_CLASS} />
                    </Button>
                </div>

                <div className="flex flex-col gap-3 px-4 pb-3">
                    {schemaFields.length > 0 && (
                        <div
                            className={`${getColSpanClass(
                                schemaFields[0].style?.layout
                            )}`}
                        >
                            <label className="text-xs font-medium mb-1.5 block">
                                {schemaFields[0].title}
                                {isRequired(schemaFields[0] as InputNode) && (
                                    <span className="text-destructive ml-1">
                                        *
                                    </span>
                                )}
                            </label>

                            <DocumentInputRenderer
                                field={schemaFields[0] as InputNode}
                                value={newItem[schemaFields[0].id] || ''}
                                onChange={(val) =>
                                    handleFieldChange(schemaFields[0].id, val)
                                }
                                context={context}
                                onContextUpdate={handleContextUpdate}
                                onKeyDown={handleKeyDown}
                                onLoadingChange={setIsLoadingConditionals}
                            />
                        </div>
                    )}

                    {schemaFields.length > 1 && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {schemaFields.slice(1).map((schemaField) => {
                                if (
                                    schemaField.hidden &&
                                    checkHidden(schemaField.hidden, newItem)
                                ) {
                                    return null;
                                }

                                return (
                                    <div
                                        key={schemaField.id}
                                        className={`flex-1 min-w-0 ${getColSpanClass(
                                            schemaField.style?.layout
                                        )}`}
                                    >
                                        <label className="text-xs font-medium mb-1.5 block">
                                            {schemaField.title}
                                            {isRequired(
                                                schemaField as InputNode
                                            ) && (
                                                <span className="text-destructive ml-1">
                                                    *
                                                </span>
                                            )}
                                        </label>

                                        <DocumentInputRenderer
                                            field={schemaField as InputNode}
                                            value={
                                                newItem[schemaField.id] || ''
                                            }
                                            onChange={(val) =>
                                                handleFieldChange(
                                                    schemaField.id,
                                                    val
                                                )
                                            }
                                            context={context}
                                            onContextUpdate={
                                                handleContextUpdate
                                            }
                                            onKeyDown={handleKeyDown}
                                            onLoadingChange={
                                                setIsLoadingConditionals
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-border/30 px-4 py-3">
                    <Button
                        onClick={handleCancelForm}
                        type="button"
                        variant="outline"
                        size="sm"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveOrUpdate}
                        disabled={disabled || isLoadingConditionals}
                        className={REPEATER_PRIMARY_ACTION_CLASS}
                        type="button"
                        variant="outline"
                        size="sm"
                    >
                        {isLoadingConditionals ? 'Loading...' : submitLabel}
                    </Button>
                </div>
            </div>
        );
    };

    const renderManualMode = () => (
        <div className="space-y-4">
            {renderManualItems()}

            {isFormOpen ? (
                renderManualForm()
            ) : (
                <Button
                    onClick={handleStartAdd}
                    disabled={disabled}
                    type="button"
                    variant="outline"
                    className={`w-full border-dashed ${REPEATER_PRIMARY_ACTION_CLASS}`}
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add {field.title || 'item'}
                </Button>
            )}
        </div>
    );

    const renderComboAddItems = () => {
        if (value.length === 0) return null;
        const displayFieldId = optionFieldId || schemaFields[0]?.id;

        return (
            <div className="space-y-2">
                {value.map((item, index) => {
                    const itemId = resolveItemId(item, index);
                    const label = displayFieldId
                        ? item?.[displayFieldId] || `Item ${index + 1}`
                        : `Item ${index + 1}`;

                    return (
                        <div key={itemId} className={REPEATER_ITEM_CLASS}>
                            <div className={REPEATER_ITEM_ACCENT_CLASS} />
                            <div className="flex-1 text-sm font-medium">
                                {label}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(itemId)}
                                disabled={disabled}
                                type="button"
                                className={DELETE_BUTTON_CLASS}
                            >
                                <X className={DELETE_ICON_CLASS} />
                            </Button>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderComboAddMode = () => {
        const primaryField = schemaFields[0] as InputNode | undefined;
        if (!primaryField) return null;

        const targetFieldId = optionFieldId || primaryField.id;

        // Only add to the list on explicit commits (option selection,
        // Enter key, or click outside the popover).  Regular onChange
        // keystrokes are ignored — the combobox manages its own
        // internal input state.
        const handleComboCommit = (val: any) => {
            if (!val) return;
            const itemToAdd: Record<string, any> = {
                [targetFieldId]: val,
            };
            addItem(itemToAdd, { toggleOnDuplicate: !!dedupeByFieldId });
        };

        return (
            <div className="space-y-4">
                <DocumentInputRenderer
                    field={primaryField}
                    value=""
                    onChange={() => {}}
                    onCommit={handleComboCommit}
                    context={context}
                    onContextUpdate={() => {}}
                    onLoadingChange={setIsLoadingConditionals}
                />
                {renderComboAddItems()}
            </div>
        );
    };

    const content = (
        <div className="flex flex-col gap-4">
            {mode === 'manual' && renderManualMode()}
            {mode === 'checklist' && renderChecklistMode()}
            {mode === 'chip-add' && renderChipMode()}
            {mode === 'combo-add' && renderComboAddMode()}
        </div>
    );

    if (mode === 'checklist') {
        return content;
    }

    return (
        <div className={REPEATER_CARD_CLASS}>{content}</div>
    );
}
